import { Bell, X, Check, Package, Truck, Tag, MessageSquare } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useNotificationStore } from '../../stores/notification'
import { formatDistanceToNow } from 'date-fns'
import type { NotificationType } from '../../types/notification'

const NOTIFICATION_ICONS: Record<NotificationType, React.ReactNode> = {
  order_placed: <Package className="w-4 h-4" />,
  order_confirmed: <Check className="w-4 h-4" />,
  order_packed: <Package className="w-4 h-4" />,
  order_shipped: <Truck className="w-4 h-4" />,
  order_out_for_delivery: <Truck className="w-4 h-4" />,
  order_delivered: <Check className="w-4 h-4" />,
  order_cancelled: <X className="w-4 h-4" />,
  stock_alert: <Bell className="w-4 h-4" />,
  promotional: <Tag className="w-4 h-4" />,
  support_reply: <MessageSquare className="w-4 h-4" />,
}

const NOTIFICATION_COLORS: Record<NotificationType, string> = {
  order_placed: 'bg-blue-100 text-blue-600',
  order_confirmed: 'bg-green-100 text-green-600',
  order_packed: 'bg-purple-100 text-purple-600',
  order_shipped: 'bg-indigo-100 text-indigo-600',
  order_out_for_delivery: 'bg-orange-100 text-orange-600',
  order_delivered: 'bg-green-100 text-green-600',
  order_cancelled: 'bg-red-100 text-red-600',
  stock_alert: 'bg-yellow-100 text-yellow-600',
  promotional: 'bg-pink-100 text-pink-600',
  support_reply: 'bg-cyan-100 text-cyan-600',
}

export default function NotificationDropdown() {
  const [isOpen, setIsOpen] = useState(false)
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotificationStore()

  const recentNotifications = notifications.slice(0, 5)

  const handleNotificationClick = (id: string) => {
    markAsRead(id)
    setIsOpen(false)
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-md hover:bg-gray-100 transition-colors"
      >
        <Bell className="w-5 h-5 text-gray-700" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 top-full mt-2 w-96 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="font-semibold">Notifications</h3>
              {unreadCount > 0 && (
                <button onClick={markAllAsRead} className="text-sm text-blue-600 hover:underline">
                  Mark all as read
                </button>
              )}
            </div>

            <div className="max-h-96 overflow-y-auto">
              {recentNotifications.length === 0 ? (
                <div className="p-8 text-center">
                  <Bell className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-600">No notifications yet</p>
                </div>
              ) : (
                <div>
                  {recentNotifications.map((notification) => (
                    <Link
                      key={notification.id}
                      to={notification.link || '/notifications'}
                      onClick={() => handleNotificationClick(notification.id)}
                      className={`block p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors ${!notification.is_read ? 'bg-blue-50' : ''}`}
                    >
                      <div className="flex gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${NOTIFICATION_COLORS[notification.type]}`}>
                          {NOTIFICATION_ICONS[notification.type]}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <h4 className="font-medium text-sm">{notification.title}</h4>
                            {!notification.is_read && (
                              <div className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0 mt-1" />
                            )}
                          </div>
                          <p className="text-sm text-gray-600 line-clamp-2">{notification.message}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                          </p>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {notifications.length > 5 && (
              <div className="p-3 border-t border-gray-200 text-center">
                <Link to="/notifications" onClick={() => setIsOpen(false)} className="text-sm text-blue-600 hover:underline">
                  View all notifications
                </Link>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
